import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { SceneBackground, GradientBackground, ReportSlidesBackground } from '../shared/components/SceneBackground';
import { SyncedCaption } from '../shared/components/SyncedCaption';
import { LogoScene } from '../shared/components/LogoScene';
import {
  SCENES,
  LOGO_SCENE,
  REPORT_SLIDES,
  msToFrames,
  type Scene,
} from './types';

// Toggle between AI-generated backgrounds and gradient fallback
// Set to true after running: npx tsx scripts/generate-gdm-v3-images.ts
const USE_AI_BACKGROUNDS = true;

/**
 * Scene component that renders ONLY the background (no captions)
 * Captions are rendered by GlobalCaptions using global time for accurate timing
 */
interface SceneComponentProps {
  scene: Scene;
}

const SceneComponent: React.FC<SceneComponentProps> = ({ scene }) => {
  const { fps } = useVideoConfig();

  const sceneDurationFrames = msToFrames(scene.endMs - scene.startMs, fps);

  return (
    <AbsoluteFill>
      {/* Background only - captions handled globally */}
      {USE_AI_BACKGROUNDS ? (
        scene.id === 7 && REPORT_SLIDES.length > 0 ? (
          <ReportSlidesBackground
            imagePaths={REPORT_SLIDES}
            durationInFrames={sceneDurationFrames}
            overlayOpacity={0.35}
          />
        ) : (
          <SceneBackground
            imagePath={scene.backgroundImage}
            kenBurns={scene.kenBurns}
            durationInFrames={sceneDurationFrames}
            overlayOpacity={0.45}
          />
        )
      ) : (
        <GradientBackground
          primaryColor={scene.primaryColor}
        />
      )}
    </AbsoluteFill>
  );
};

/**
 * Global captions component - renders against global time
 * Uses the composition's global frame for accurate audio sync
 */
const GlobalCaptions: React.FC = () => {
  const frame = useCurrentFrame(); // This is the GLOBAL frame!
  const { fps } = useVideoConfig();

  // Convert global frame to absolute milliseconds
  const currentMs = (frame / fps) * 1000;

  // Find current scene based on absolute timing
  const currentScene = SCENES.find(
    (scene) => currentMs >= scene.startMs && currentMs < scene.endMs
  );

  // Don't render captions during logo scene or if no scene found
  if (!currentScene) return null;

  return (
    <SyncedCaption
      words={currentScene.words}
      currentMs={currentMs}
      primaryColor={currentScene.primaryColor}
      accentColor={currentScene.accentColor}
      fontSize={72}
      highlightCurrentWord={true}
      layout="center"
    />
  );
};

/**
 * Main GDM Quiet Phone V5 Composition
 *
 * Key improvements over V2:
 * 1. Perfect audio sync using word-level timing data
 * 2. AI-generated background images with Ken Burns effect
 * 3. Scene-specific color palettes for visual variety
 * 4. Straight cuts between scenes for a calmer edit
 * 5. Single-line captions for better readability
 */
export const GDMQuietPhoneV5: React.FC = () => {
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill>
      {/* Voiceover audio - main speech track */}
      <Audio
        src={staticFile('audio/gdm-quiet-phone-voiceover-v5.mp3')}
        volume={1.0}
      />

      {/* Background music - stops before logo scene (logo has its own audio) */}
      <Sequence durationInFrames={msToFrames(LOGO_SCENE.startMs, fps)}>
        <Audio
          src={staticFile('audio/gdm-quiet-phone/background-music.mp3')}
          volume={0.12}
          startFrom={0}
        />
      </Sequence>

      {/* Backgrounds with straight cuts */}
      {SCENES.map((scene) => (
        <Sequence
          key={scene.id}
          from={msToFrames(scene.startMs, fps)}
          durationInFrames={msToFrames(scene.endMs - scene.startMs, fps)}
        >
          <SceneComponent scene={scene} />
        </Sequence>
      ))}

      {/* Logo sequence at the end */}
      <Sequence
        from={msToFrames(LOGO_SCENE.startMs, fps)}
        durationInFrames={msToFrames(LOGO_SCENE.endMs - LOGO_SCENE.startMs, fps)}
      >
        <LogoScene logoVideoPath="logos/garage-marketers.mp4" />
      </Sequence>

      {/* Captions use GLOBAL frame - OUTSIDE TransitionSeries for accurate audio sync */}
      <GlobalCaptions />
    </AbsoluteFill>
  );
};

// Export default for easier importing
export default GDMQuietPhoneV5;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
